import { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import type { LinkPayload, Folder } from '../api';
import type { TranslationKey } from '../i18n';

interface Props {
  mode: 'create' | 'edit';
  onSubmit: (values: LinkPayload) => Promise<void> | void;
  onCancel?: () => void;
  initialValues?: LinkPayload;
  submitting?: boolean;
  shortDomain?: string;
  disabled?: boolean;
  notice?: {
    title: string;
    body: string;
    actionLabel?: string;
    onAction?: () => void;
  };
  t: (key: TranslationKey) => string;
  isPro?: boolean;
  folders?: Folder[];
  onCreateFolder?: (name: string) => Promise<Folder | null>;
}

export const LinkForm = ({
  mode,
  onSubmit,
  onCancel,
  initialValues,
  submitting,
  shortDomain,
  disabled,
  notice,
  t,
  isPro = false,
  folders = [],
  onCreateFolder,
}: Props) => {
  const baseValues: LinkPayload = {
    slug: '',
    destination: '',
    description: '',
    password: '',
    folderId: null,
    ...(initialValues || {}),
  };

  const [values, setValues] = useState<LinkPayload>(baseValues);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    setValues({
      slug: initialValues?.slug ?? '',
      destination: initialValues?.destination ?? '',
      description: initialValues?.description ?? '',
      folderId: initialValues?.folderId ?? null,
    });
    // Reset folder creation state when switching links
    setIsCreatingFolder(false);
    setNewFolderName('');
  }, [initialValues]);

  const handleChange = (field: keyof LinkPayload) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!values.destination) return;

    let finalFolderId = values.folderId;

    // Handle new folder creation
    if (isCreatingFolder && newFolderName.trim() && onCreateFolder) {
      try {
        const newFolder = await onCreateFolder(newFolderName.trim());
        if (newFolder) {
          finalFolderId = newFolder.id;
        }
      } catch (err) {
        console.error('Failed to create folder inline', err);
        // Continue without folder or show error? 
        // Ideally stop. But returning here might be silent.
        // Assume onCreateFolder handles error alerts or we just fail.
        return;
      }
    }

    const payload: LinkPayload = {
      ...values,
      destination: values.destination.trim(),
      slug: values.slug?.trim(),
      description: values.description?.trim() || null,
      folderId: finalFolderId || null,
    };
    await onSubmit(payload);
  };

  const previewDomain = shortDomain?.replace(/\/$/, '') || 'https://votre-domaine.com';
  const slugLabel = mode === 'create' ? t('linkForm.slugOptional') : t('linkForm.slug');

  const isDisabled = Boolean(disabled || submitting);

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Auto-collapse on mobile when not editing/submitting to save space
  // NEVER collapse in edit mode
  useEffect(() => {
    if (mode === 'edit') {
      setIsCollapsed(false);
      return;
    }
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches) {
      if (mode === 'create' && !values.destination) {
        setIsCollapsed(true);
      }
    }
  }, [mode]);

  return (
    <form onSubmit={handleSubmit} className={`card grid ${isCollapsed ? 'collapsed' : ''}`}>
      <div
        className="card-header-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h2>{mode === 'create' ? t('linkForm.createTitle') : t('linkForm.editTitle')}</h2>
        <button type="button" className="ghost-button icon-button" style={{ fontSize: '1.25rem' }}>
          {isCollapsed ? '+' : '−'}
        </button>
      </div>

      {!isCollapsed && (
        <>
          {notice ? (
            <div className="inline-notice" role="note">
              <div className="inline-notice-text">
                <p className="inline-notice-title">{notice.title}</p>
                <p className="inline-notice-body">{notice.body}</p>
              </div>
              {notice.actionLabel && notice.onAction ? (
                <button className="primary" type="button" onClick={notice.onAction}>
                  {notice.actionLabel}
                </button>
              ) : null}
            </div>
          ) : null}
          <div>
            <label>{t('linkForm.destinationLabel')}</label>
            <input
              type="url"
              placeholder={t('linkForm.destinationPlaceholder')}
              value={values.destination}
              onChange={handleChange('destination')}
              required
              disabled={isDisabled}
            />
          </div>
          <div>
            <label>{t('linkForm.folderLabel') || 'Dossier'}</label>
            <div className="select-wrapper" style={{ position: 'relative' }}>
              <button
                type="button"
                className="input-field select-trigger"
                onClick={() => {
                  const el = document.getElementById('folder-dropdown');
                  if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                }}
                disabled={isDisabled}
                style={{ textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {values.folderId ? (
                    (() => {
                      const f = folders.find(f => Number(f.id) === Number(values.folderId));
                      return f ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" /></svg>
                          {f.name}
                        </>
                      ) : (t('linkForm.noFolder') || 'Aucun dossier');
                    })()
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0 }}><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" /></svg>
                      {t('linkForm.noFolder') || 'Aucun dossier'}
                    </>
                  )}
                </span>
                <span style={{ opacity: 0.5 }}>▼</span>
              </button>

              <div
                id="folder-dropdown"
                className="custom-dropdown-menu"
                style={{
                  display: 'none',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 200,
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '0.75rem',
                  marginTop: '4px',
                  boxShadow: 'var(--shadow)',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                <div
                  className="dropdown-item"
                  onClick={() => {
                    setIsCreatingFolder(false);
                    setValues((prev) => ({ ...prev, folderId: null }));
                    const el = document.getElementById('folder-dropdown');
                    if (el) el.style.display = 'none';
                  }}
                  style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid var(--border)' }}
                >
                  <span style={{ width: '16px', display: 'inline-block' }}>{values.folderId === null ? '✓' : ''}</span>
                  {t('linkForm.noFolder') || 'Aucun dossier'}
                </div>

                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="dropdown-item"
                    onClick={() => {
                      setIsCreatingFolder(false);
                      setValues((prev) => ({ ...prev, folderId: folder.id }));
                      const el = document.getElementById('folder-dropdown');
                      if (el) el.style.display = 'none';
                    }}
                    style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid var(--border)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" /></svg>
                    {folder.name}
                    {values.folderId === folder.id && <span style={{ marginLeft: 'auto' }}>✓</span>}
                  </div>
                ))}

                {onCreateFolder && (
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setIsCreatingFolder(true);
                      setValues((prev) => ({ ...prev, folderId: null }));
                      const el = document.getElementById('folder-dropdown');
                      if (el) el.style.display = 'none';
                    }}
                    style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent-2)' }}
                  >
                    <span>➕</span>
                    {t('folder.create') || 'Nouveau dossier...'}
                  </div>
                )}
              </div>

              {/* Overlay to close dropdown when clicking outside */}
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'none',
                  zIndex: 199
                }}
                id="folder-dropdown-overlay"
                onClick={() => {
                  const el = document.getElementById('folder-dropdown');
                  if (el) el.style.display = 'none';
                  const overlay = document.getElementById('folder-dropdown-overlay');
                  if (overlay) overlay.style.display = 'none';
                }}
              />
              {/* Script to toggle overlay */}
              <script dangerouslySetInnerHTML={{
                __html: `
                document.querySelector('.select-trigger')?.addEventListener('click', () => {
                  const overlay = document.getElementById('folder-dropdown-overlay');
                  if (overlay) overlay.style.display = overlay.style.display === 'none' ? 'block' : 'none';
                });
              `}} />
            </div>
          </div>

          {isCreatingFolder && (
            <div style={{ marginTop: '0.5rem' }}>
              <label>{t('folder.new') || 'Nouveau dossier'}</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Ex: Marketing, Projets..."
                  required
                  className="input-field"
                  autoFocus
                />
                <button type="button" className="secondary icon-button" onClick={() => { setIsCreatingFolder(false); setValues(prev => ({ ...prev, folderId: null })); }} title="Annuler">✕</button>
              </div>
            </div>
          )}

          <div>
            <label>{slugLabel}</label>
            <div className="input-group">
              <span className="input-prefix">{previewDomain}/</span>
              <input
                type="text"
                placeholder={t('linkForm.slugPlaceholder')}
                value={values.slug ?? ''}
                onChange={handleChange('slug')}
                disabled={isDisabled}
                className="slug-input"
              />
            </div>
          </div>
          <div>
            <label>{t('linkForm.descriptionLabel')}</label>
            <textarea
              placeholder={t('linkForm.descriptionPlaceholder')}
              value={values.description ?? ''}
              onChange={handleChange('description')}
              disabled={isDisabled}
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {t('pricing.passwordTitle')}

            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="password"
                placeholder={t('pricing.passwordDesc')}
                value={values.password ?? ''}
                onChange={handleChange('password')}
                disabled={isDisabled}
              />

            </div>
          </div>
          <div className="button-row">
            <button className="primary" type="submit" disabled={isDisabled}>
              {isDisabled ? t('linkForm.generating') : mode === 'create' ? t('linkForm.generate') : t('linkForm.update')}
            </button>
            {mode === 'edit' && (
              <button className="secondary" type="button" onClick={onCancel}>
                {t('linkForm.cancel')}
              </button>
            )}
          </div>
        </>
      )}
    </form>
  );
};

export default LinkForm;
